<template>
  <div
    class="list-item"
    :class="{ 'is-out-of-stock': isOutOfStock }"
    @click="navigateToDetails"
  >
    <!-- Thumbnail -->
    <div class="list-item__thumb">
      <img
        v-if="getImageUrl(part)"
        :src="getImageUrl(part)"
        :alt="part.name"
        @error="handleImageError"
      />
      <div v-else class="list-item__no-img">
        <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
        </svg>
      </div>
      <span v-if="isOutOfStock" class="list-item__out-badge">OUT</span>
    </div>

    <!-- Content -->
    <div class="list-item__content">
      <!-- Info -->
      <div class="list-item__info">
        <div class="list-item__title">{{ part.name }}</div>
        <span class="list-item__ref">{{ part.part_number }}</span>
        <div class="list-item__specs">
          <div v-if="part.brand" class="list-item__spec">
            <span class="list-item__spec-label">Brand</span>
            <span class="list-item__spec-value">{{ part.brand.name }}</span>
          </div>
          <div class="list-item__spec">
            <span class="list-item__spec-label">Stock</span>
            <span :class="['list-item__spec-value', stockClass]">{{ part.stock_quantity }}</span>
          </div>
          <div v-if="part.category" class="list-item__spec">
            <span class="list-item__spec-label">Category</span>
            <span class="list-item__spec-value">{{ part.category.name }}</span>
          </div>
        </div>
      </div>

      <!-- Status -->
      <div class="list-item__status">
        <span :class="['status-badge', statusBadge.class]">{{ statusBadge.label }}</span>
      </div>

      <!-- Action -->
      <div class="list-item__action" @click.stop>
        <button
          @click="emit('inquire', part)"
          :disabled="isOutOfStock"
          class="btn-quote"
          :class="{ 'btn-disabled': isOutOfStock }"
        >
          {{ translations.inquire }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { getSafeImageUrl, handleImageError } = useImageHandling();
const { getSparePartStatusLabel } = useStatusBadge();

const { t } = useI18n();

const props = defineProps({
  part: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['inquire']);

const translations = computed(() => {
  try {
    return {
      brand: t('spareParts.card.brand') || 'Brand',
      stockQuantity: t('spareParts.card.stockQuantity') || 'Stock',
      compatible: t('spareParts.card.compatible') || 'Compatible',
      minStock: t('spareParts.card.minStock') || 'Min Stock',
      partNumber: t('spareParts.card.partNumber') || 'Part #',
      inquire: t('spareParts.card.inquire') || 'Get Price Quotation'
    };
  } catch {
    return {
      brand: 'Brand',
      stockQuantity: 'Stock',
      compatible: 'Compatible',
      minStock: 'Min Stock',
      partNumber: 'Part #',
      inquire: 'Get Price Quotation'
    };
  }
});

const isOutOfStock = computed(() => props.part.stock_quantity <= 0);

const stockClass = computed(() => {
  if (props.part.stock_quantity <= 0) return 'text-red-600 font-bold';
  if (props.part.stock_quantity <= (props.part.minimum_stock || 5)) return 'text-amber-600 font-semibold';
  return 'text-emerald-600 font-semibold';
});

const statusBadge = computed(() => {
  const label = getSparePartStatusLabel(props.part.stock_quantity, props.part.minimum_stock);
  return {
    label,
    class: getStatusBadgeClass(props.part.stock_quantity, props.part.minimum_stock)
  };
});

const getStatusBadgeClass = (stockQuantity, minimumStock) => {
  if (stockQuantity <= 0) return 'badge-out-of-stock';
  if (stockQuantity <= (minimumStock || 5)) return 'badge-low-stock';
  return 'badge-in-stock';
};

const getImageUrl = (part) => {
  if (part.images && part.images.length > 0) {
    const img = part.images[0];
    // Prefer file_path, always process through getSafeImageUrl
    if (img.file_path) return getSafeImageUrl(img.file_path);
    if (img.image_url) return getSafeImageUrl(img.image_url);
    if (img.url) return getSafeImageUrl(img.url);
  }
  if (part.image_url) {
    return getSafeImageUrl(part.image_url);
  }
  return null;
};

const navigateToDetails = () => {
  navigateTo(`/spare-parts/${props.part.id}`);
};
</script>

<style scoped>
/* List Item */
.list-item {
  display: flex;
  align-items: stretch;
  background: white;
  border: 2px solid #d1d5db;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  height: 180px;
}

.list-item:hover {
  border-color: #9ca3af;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.list-item.is-out-of-stock {
  opacity: 0.65;
}

/* Thumbnail */
.list-item__thumb {
  position: relative;
  width: 350px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
}

.list-item__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.list-item__no-img {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

.list-item__out-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 4px 8px;
  background: #dc2626;
  color: white;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  border-radius: 4px;
}

/* Content */
.list-item__content {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 16px 20px;
  min-width: 0;
}

/* Info */
.list-item__info {
  flex: 1;
  min-width: 0;
}

.list-item__title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.list-item__ref {
  display: inline-block;
  font-size: 12px;
  font-family: 'SF Mono', Monaco, monospace;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 4px;
  margin-bottom: 10px;
}

.list-item__specs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
}

.list-item__spec {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.list-item__spec-label {
  color: #059669;
  font-weight: 600;
}

.list-item__spec-value {
  color: #334155;
}

.stock-good {
  color: #059669;
  font-weight: 700;
}

.stock-low {
  color: #d97706;
  font-weight: 700;
}

.stock-out {
  color: #dc2626;
  font-weight: 700;
}

/* Status */
.list-item__status {
  flex-shrink: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.status-badge::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.badge-in-stock {
  background: #ecfdf5;
  color: #059669;
}

.badge-low-stock {
  background: #fffbeb;
  color: #d97706;
}

.badge-out-of-stock {
  background: #fef2f2;
  color: #dc2626;
}

/* Action */
.list-item__action {
  flex-shrink: 0;
}

.btn-quote {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;
}

.btn-quote:hover:not(.btn-disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.35);
}

.btn-quote.btn-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #94a3b8;
}

/* Responsive */
@media (max-width: 768px) {
  .list-item {
    flex-direction: column;
    height: auto;
  }
  
  .list-item__thumb {
    width: 100%;
    height: 200px;
  }
  
  .list-item__content {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .list-item__status {
    order: -1;
  }
  
  .btn-quote {
    width: 100%;
    justify-content: center;
  }
}
</style>
